/**
 * The screenshot under the map (FD §3.5, Advanced): add one, line it up (OverlayCalibrator), see-through
 * slider, turn and nudge, line up again, replace or remove. One screenshot per plan.
 */
import { useEffect, useRef, useState } from 'react';
import * as api from '../../../api';
import { setOverlay, setPlot, updateOverlay } from '../../../../core/ops';
import type { PlanDoc } from '../../../../shared/types';
import { useEditor, type EditorStore } from '../../../store/editor';
import { Dialog, errorText, toast } from '../../shell/dialogs';
import { OverlayCalibrator, type CalibrationResult } from '../overlay/OverlayCalibrator';
import { downscaleImage } from '../overlay/downscale';

interface Pending { blob: Blob | null; url: string; askBoundary: boolean; recalibrate: boolean }

/** The overlay panel. */
export function OverlayPanel({ store }: { store: EditorStore }) {
  const overlay = useEditor(store, (s) => s.doc.overlay);
  const size = useEditor(store, (s) => s.doc.size);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [traceToo, setTraceToo] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => { if (pending?.blob) URL.revokeObjectURL(pending.url); }, [pending]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const blob = await downscaleImage(file);
      setPending({ blob, url: URL.createObjectURL(blob), askBoundary: traceToo, recalibrate: false });
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const done = async (r: CalibrationResult) => {
    const p = pending;
    setPending(null);
    if (!p) return;
    try {
      let file = overlay?.file ?? '';
      if (p.blob) {
        setBusy(true);
        file = (await api.uploadOverlay(store.planId, p.blob)).file;
      }
      store.apply((d: PlanDoc) => {
        if (p.recalibrate && d.overlay) updateOverlay(d, r.overlay);
        else setOverlay(d, { overlay: { file, ...r.overlay } });
        if (r.boundary && r.boundary.length >= 3) setPlot(d, { points: r.boundary, smooth: d.plot?.smooth ?? true });
      }, undefined, { label: p.recalibrate ? 'Line up screenshot' : 'Add screenshot' });
      store.say(r.boundary ? 'Screenshot added and the zoo boundary traced. Re-arrange habitats to fit them inside it.' : 'Screenshot lined up under the map.');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const nudge = (dx: number, dy: number) => overlay && store.apply(updateOverlay, { x: overlay.x + dx, y: overlay.y + dy }, { label: 'Move screenshot', group: 'overlay-move' });
  const turn = (deg: number) => {
    if (!overlay) return;
    // turn around the image's middle as it sits on the map, not its corner
    const img = imageSize.current;
    const cx = img ? img.w / 2 : 0, cy = img ? img.h / 2 : 0;
    const r0 = (overlay.rotation * Math.PI) / 180, r1 = ((overlay.rotation + deg) * Math.PI) / 180, s = overlay.scale;
    const mid = [overlay.x + s * (cx * Math.cos(r0) - cy * Math.sin(r0)), overlay.y + s * (cx * Math.sin(r0) + cy * Math.cos(r0))];
    store.apply(updateOverlay, {
      rotation: overlay.rotation + deg,
      x: mid[0]! - s * (cx * Math.cos(r1) - cy * Math.sin(r1)),
      y: mid[1]! - s * (cx * Math.sin(r1) + cy * Math.cos(r1)),
    }, { label: 'Turn screenshot', group: 'overlay-turn' });
  };
  const imageSize = useRef<{ w: number; h: number } | null>(null);
  useEffect(() => {
    if (!overlay) return;
    const img = new Image();
    img.onload = () => { imageSize.current = { w: img.naturalWidth, h: img.naturalHeight }; };
    img.src = api.overlayUrl(store.planId, overlay.file);
  }, [overlay?.file, store.planId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="panel">
      <h3>Screenshot under the map</h3>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => void pick(e.target.files?.[0])} />
      {!overlay ? (
        <>
          <p className="hint small">Put a top-down screenshot of your zoo under the plan, and line it up so sizes match the game.</p>
          <label className="check small"><input type="checkbox" checked={traceToo} onChange={(e) => setTraceToo(e.target.checked)} /> Also trace the zoo boundary on it</label>
          <div className="actions"><button type="button" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Preparing…' : 'Add a screenshot…'}</button></div>
        </>
      ) : (
        <>
          <label className="field">
            <span>See-through: {Math.round(overlay.opacity * 100)}%</span>
            <input type="range" min={0} max={1} step={0.05} value={overlay.opacity} onChange={(e) => store.apply(updateOverlay, { opacity: Number(e.target.value) }, { label: 'Screenshot see-through', group: 'overlay-opacity' })} />
          </label>
          <div className="row wrap">
            <span className="nudge" aria-label="Nudge the screenshot">
              <button type="button" title="Move 1 m left" onClick={() => nudge(-1, 0)}>←</button>
              <button type="button" title="Move 1 m up" onClick={() => nudge(0, -1)}>↑</button>
              <button type="button" title="Move 1 m down" onClick={() => nudge(0, 1)}>↓</button>
              <button type="button" title="Move 1 m right" onClick={() => nudge(1, 0)}>→</button>
            </span>
            <span className="nudge">
              <button type="button" title="Turn a little to the left" onClick={() => turn(-0.5)}>⟲</button>
              <button type="button" title="Turn a little to the right" onClick={() => turn(0.5)}>⟳</button>
            </span>
          </div>
          <div className="actions">
            <button type="button" onClick={() => setPending({ blob: null, url: api.overlayUrl(store.planId, overlay.file), askBoundary: false, recalibrate: true })}>Line up again</button>
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}>Replace…</button>
            <button type="button" className="danger-text" onClick={() => store.apply(setOverlay, { overlay: null }, { label: 'Remove screenshot' })}>Remove</button>
          </div>
        </>
      )}
      {pending && (
        <Dialog title={pending.recalibrate ? 'Line up the screenshot' : 'Line up your screenshot'} onClose={() => setPending(null)} wide>
          <OverlayCalibrator
            imageUrl={pending.url}
            mapSize={size}
            askBoundary={pending.askBoundary}
            {...(pending.recalibrate && overlay ? { initial: { x: overlay.x, y: overlay.y, scale: overlay.scale, rotation: overlay.rotation, opacity: overlay.opacity } } : {})}
            onDone={(r) => void done(r)}
            onCancel={() => setPending(null)}
          />
        </Dialog>
      )}
    </section>
  );
}
